import { Button, EmptyState, Spinner } from '@ohear/ui'
import { Outlet, createRootRoute } from '@tanstack/react-router'
import { Suspense } from 'react'
import { Layout } from '../components/Layout'
import { useAuth } from '../lib/auth'

/**
 * Root route. Wraps every page in the app `<Layout>` chrome and gates rendering
 * on the initial Supabase `getSession()` resolution so child routes never see a
 * flash of "signed-out" UI for a user who is in fact signed in.
 *
 * Also provides the top-level Suspense boundary that catches `lazy()` chunks
 * loaded by per-route components (recording, project-setup, supervisor takes,
 * etc). Per-route fallbacks override this for nicer in-context spinners.
 *
 * `errorComponent` overrides TanStack Router's default raw-red error panel so
 * that uncaught route errors (e.g. `useRequireRole` throwing for an anonymous
 * visitor on /actor) render in our Apple-light EmptyState chrome instead of
 * a debug-style red box.
 */
export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
  notFoundComponent: RootNotFoundComponent,
})

function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  // NotAuthorizedError is the role-guard signal; treat it as a sign-in prompt
  // rather than a generic crash so anonymous visitors see a friendly nudge.
  const isAuth = error?.name === 'NotAuthorizedError'
  return (
    <Layout>
      <div className="p-6">
        <EmptyState
          title={isAuth ? 'Please sign in' : 'Something went wrong on this page'}
          description={
            isAuth
              ? 'You need to be signed in to view this page. Sign in to continue.'
              : "We've logged the problem and our team will take a look. You can try the page again, or head back to the dashboard."
          }
          action={
            <div className="flex gap-2">
              {isAuth ? (
                <Button onClick={() => location.assign('/login')}>Sign in</Button>
              ) : (
                <>
                  <Button onClick={reset}>Try again</Button>
                  <Button variant="ghost" onClick={() => location.assign('/')}>
                    Go to dashboard
                  </Button>
                </>
              )}
            </div>
          }
        />
      </div>
    </Layout>
  )
}

function RootNotFoundComponent() {
  return (
    <Layout>
      <div className="p-6">
        <EmptyState
          title="Page not found"
          description="The page you were looking for does not exist or has moved."
          action={<Button onClick={() => location.assign('/')}>Go to dashboard</Button>}
        />
      </div>
    </Layout>
  )
}

function RootComponent() {
  const { loading } = useAuth()
  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center p-12">
          <Spinner size="lg" label="Loading…" />
        </div>
      </Layout>
    )
  }
  return (
    <Layout>
      <Suspense
        fallback={
          <div className="flex justify-center p-12">
            <Spinner size="lg" label="Loading…" />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </Layout>
  )
}
